import { useEffect, useState } from 'react';
import { Link2 } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PainelPermissionsDialogShell } from '@/components/painel/permissions/painel-role-visuals';
import {
  painelButtonPrimaryClass,
  painelModalCancelButtonClass,
} from '@/components/painel/painel-styles';
import { painelInputClass } from '@/components/painel/cms/painel-form';
import { cn } from '@/lib/utils';

export type InsertLinkDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Texto seleccionado no editor (pré-preenche o rótulo). */
  selectedText?: string;
  onInsert: (payload: { url: string; text: string }) => void;
};

function normalizeUrl(raw: string): string {
  const t = raw.trim();
  if (!t) return '';
  if (/^(https?:|mailto:|tel:|\/|#)/i.test(t)) return t;
  return `https://${t}`;
}

export function InsertLinkDialog({
  open,
  onOpenChange,
  selectedText = '',
  onInsert,
}: InsertLinkDialogProps) {
  const [url, setUrl] = useState('https://');
  const [text, setText] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setUrl('https://');
    setText(selectedText.trim());
    setError('');
  }, [open, selectedText]);

  const handleInsert = () => {
    const href = normalizeUrl(url);
    if (!href || href === 'https://') {
      setError('Indique um URL válido.');
      return;
    }
    const label = text.trim() || href.replace(/^https?:\/\//i, '');
    onInsert({ url: href, text: label });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="max-h-[90vh] max-w-md overflow-hidden border-0 bg-[#030023] p-0 shadow-none"
      >
        <DialogTitle className="sr-only">Inserir link</DialogTitle>
        <PainelPermissionsDialogShell>
          <div className="shrink-0 border-b border-white/10 bg-sky-950/20 px-6 pb-5 pt-6 sm:px-7">
            <div className="flex items-start gap-4 pr-12">
              <span className="inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-500/25 to-sky-700/10 ring-1 ring-sky-400/35 shadow-[0_0_40px_-10px_rgba(56,189,248,0.35)]">
                <Link2 className="h-6 w-6 text-sky-200" aria-hidden />
              </span>
              <div className="min-w-0">
                <p className="font-inter text-[10px] font-bold uppercase tracking-[0.18em] text-sky-300/55">
                  Editor
                </p>
                <h2 className="mt-1 font-poppins text-xl font-bold text-white">Inserir link</h2>
                <p className="mt-1.5 font-inter text-sm leading-relaxed text-slate-400">
                  Adicione um URL e, opcionalmente, o texto visível do link.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-4 px-6 py-5 sm:px-7">
            <div className="space-y-2">
              <Label htmlFor="insert-link-url" className="font-poppins text-slate-300">
                URL
              </Label>
              <Input
                id="insert-link-url"
                value={url}
                onChange={(e) => {
                  setUrl(e.target.value);
                  setError('');
                }}
                onKeyDown={(e) => e.key === 'Enter' && handleInsert()}
                placeholder="https://…"
                autoFocus
                className={painelInputClass}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="insert-link-text" className="font-poppins text-slate-300">
                Texto do link
              </Label>
              <Input
                id="insert-link-text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleInsert()}
                placeholder="Texto visível (opcional)"
                className={painelInputClass}
              />
            </div>
            {error ? (
              <p className="rounded-xl border border-rose-400/30 bg-rose-500/10 px-3 py-2 font-inter text-xs text-rose-200">
                {error}
              </p>
            ) : null}
          </div>

          <div className="flex flex-col-reverse gap-2 border-t border-white/10 bg-blue-950/25 px-6 py-4 sm:flex-row sm:justify-end sm:gap-3 sm:px-7">
            <Button
              type="button"
              variant="ghost"
              className={painelModalCancelButtonClass}
              onClick={() => onOpenChange(false)}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              className={cn(painelButtonPrimaryClass, 'h-10 px-5')}
              onClick={handleInsert}
            >
              Inserir link
            </Button>
          </div>
        </PainelPermissionsDialogShell>
      </DialogContent>
    </Dialog>
  );
}
